/* eslint-disable react/prop-types */
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import DataDrawer from "../drawers/data-drawer";
import DataBeneficiaries from "@/routes/dashboard/buy-data/DataBeneficiaries";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { Button } from "../ui/button";
import { Fingerprint, ScanFace } from "lucide-react";
import { set } from "react-hook-form";

const BuyDataForm = ({
  plans,
  onSubmit,
  handleSubmit,
  register,
  errors,
  setValue,
  watch,
  telcos,
  loading,
  beneficiaryData,
  openDataDrawer,
  setOpenDataDrawer,
}) => {
  const formatter = new Intl.NumberFormat("en-US");
  const user = useSelector((state) => state.user.user);
  const [pinError, setPinError] = useState("");
  // const [drawerOpen, setDrawerOpen] = useState(false);
  const platform = Capacitor.getPlatform();

  const phone = watch("phone");
  const network = watch("carrier");
  const plan = watch("plan");
  const pin = watch("pin");

  const phoneIsValid = phone && /^[0-9]{11,12}$/.test(phone);

  // Validate PIN as user types
  useEffect(() => {
    if (pin) {
      const userPin = user.pin.toString();
      if (pin === userPin) {
        setPinError("");
      } else {
        setPinError("Invalid transaction pin");
      }
    } else {
      setPinError("");
    }
  }, [pin, user.pin]);

  // Fixed isFormValid logic - now returns true when form is valid
  const isFormValid =
    network && plan && phoneIsValid && pin && pin === user.pin.toString();

  async function authenticateWithBiometrics() {
    const result = await NativeBiometric.isAvailable();

    if (!result.isAvailable) {
      toast.warning(
        "Biometric authentication is not available on this device. Kindly enable biometrics in your settings.",
      );
    }

    const verified = await NativeBiometric.verifyIdentity({
      reason: "Verify Transaction",
      title: "Data Purchase",
      subtitle: "Please authenticate to proceed with the data bundle purchase.",
    })
      .then(() => true)
      .catch(() => false);

    if (verified) {
      setValue("pin", user.pin.toString());
      setOpenDataDrawer(true);
    } else {
      toast.error("Authentication failed. Please try again.");
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <section className="grid grid-cols-1 gap-2">
        <div className="col-span-1">
          <label htmlFor="network" className="text-sm font-semibold">
            Network
          </label>
          <Select
            value={watch("carrier")}
            onValueChange={(value) => {
              setValue("carrier", value);
              setValue("plan", null);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Network" />
            </SelectTrigger>
            <SelectContent>
              {telcos.map(
                (telco, index) =>
                  telco && (
                    <SelectItem key={index} value={telco}>
                      {telco.toUpperCase()}
                    </SelectItem>
                  ),
              )}
            </SelectContent>
          </Select>
        </div>
        {watch("carrier") ? (
          <div className="col-span-1 pb-5">
            <label htmlFor="network" className="text-sm font-semibold">
              Select Data Plan
            </label>

            <div className="col-span-1 mt-2 grid grid-cols-3 gap-4 lg:grid-cols-7">
              {plans &&
                plans
                  .sort((a, b) => a?.price - b?.price)
                  .map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() => {
                        setValue("plan", plan.id);
                      }}
                      className={cn(
                        "dark:bg-darkInput flex cursor-pointer flex-col items-center gap-1 rounded-xl bg-gray-100 p-4 text-center hover:bg-gray-200",
                        {
                          "border-primary border-2": watch("plan") === plan.id,
                        },
                      )}
                    >
                      <h3 className="font-semibold">{plan.capacity}</h3>

                      <p className="text-xs">{plan.duration}</p>

                      <h2 className="font-inter text-sm font-medium">
                        ₦{formatter.format(plan.price)}
                      </h2>
                    </div>
                  ))}
            </div>
          </div>
        ) : (
          <div className="py-4 text-center font-medium dark:text-white">
            Select a network to see data bundles
          </div>
        )}

        <div className="col-span-1">
          <label htmlFor="phone" className="text-sm font-semibold">
            Phone Number
          </label>
          <div className="dark:border-darkBorder dark:bg-darkInput mt-1 flex items-center gap-x-3 rounded-md border p-2 py-2.5">
            <input
              type="tel"
              autoComplete="off"
              {...register("phone", {
                required: "Phone Number is required",
                pattern: {
                  value: /^[0-9]{11,12}$/,
                  message:
                    "Phone number must be 11 or 12 digits e.g 09123456789",
                },
                onChange: (e) => {
                  let value = e.target.value;
                  if (value.startsWith("+234")) {
                    value = "0" + value.slice(4);
                    e.target.value = value;
                  }
                  const cleanNumber = value.replace(/[^0-9]/g, "");
                },
              })}
              className="w-full border-none bg-transparent text-sm outline-none"
              placeholder="0812-222-3343"
            />

            <DataBeneficiaries
              watch={watch}
              setValue={setValue}
              telcos={telcos}
            />
          </div>

          {errors.phone && (
            <div className="text-xs font-medium text-red-500">
              {errors.phone.message}
            </div>
          )}

          {!beneficiaryData && (
            <div className="mt-1 flex items-center justify-end gap-1">
              <input
                type="checkbox"
                id="saveb"
                {...register("saveBeneficiary")}
              />
              <label htmlFor="saveb" className="font-geist text-sm">
                Save number as beneficiary
              </label>
            </div>
          )}

          {phone && network && plan ? (
            <div className="col-span-1">
              <label htmlFor="phone" className="text-sm font-semibold">
                Transaction Pin
              </label>
              <div className="flex gap-3">
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  className="inputClass"
                  placeholder="****"
                  {...register("pin", {
                    required: "Transaction Pin is required",
                    onChange: (e) => {
                      const inputPin = e.target.value;
                      setValue("pin", inputPin);
                    },
                  })}
                />
                {platform != "web" && (
                  <Button
                    onClick={authenticateWithBiometrics}
                    type="button"
                    className={"h-[40px] text-white"}
                  >
                    {platform == "android" ? (
                      <Fingerprint size={30} />
                    ) : (
                      <ScanFace size={30} />
                    )}{" "}
                    Use Biometrics
                  </Button>
                )}
              </div>

              {pinError && (
                <p className="text-xs font-medium text-red-500">{pinError}</p>
              )}
            </div>
          ) : (
            <></>
          )}
        </div>

        <DataDrawer
          isFormValid={isFormValid}
          phone={phone}
          network={network}
          telcos={telcos}
          setValue={setValue}
          loading={loading}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          plan={plan}
          transactionPin={pin}
          plans={plans}
          setOpenDataDrawer={setOpenDataDrawer}
          openDataDrawer={openDataDrawer}
        />
      </section>
    </form>
  );
};
export default BuyDataForm;
